import api from 'api';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEmailTemplateQuery } from 'hooks/queries';
import { useForm } from 'react-hook-form';
import { useAlert } from 'common/alertContext';
import { useTranslation } from 'react-i18next';
import { addLanguagePropertiesInForm, deleteLanguagePropertiesInForm, setValueLanguageInForm } from 'utils/translate';

const defaultValues = {
  subject: '',
  content: '',
  variableContent: '',
  fromName: '',
  targetRoles: [],
};

export default function useEditEmailTemplate(emailTemplateId) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isLoading } = useEmailTemplateQuery(emailTemplateId);
  const [showAlert, setShowAlert] = useState(true);

  const {
    handleSubmit,
    control,
    watch,
    reset,
    resetField,
    setValue,
    formState: { isDirty },
  } = useForm({ mode: 'all', defaultValues });

  const { setStateAlert } = useAlert();

  const translatedProperties = ['subject', 'variableContent'];

  useEffect(() => {
    reset({
      subject: data?.subject || undefined,
      content: data?.content || undefined,
      variableContent: data?.variableContent || undefined,
      fromName: data?.fromName || undefined,
      targetRoles: data?.targetRoles || undefined,
    });
  }, [data, reset]);

  const mutation = useMutation(api.Emails.edit, {
    onSuccess: () => {
      queryClient.invalidateQueries(['get-email-template']);
      queryClient.invalidateQueries(['get-list-emails']);
      if (showAlert) {
        setStateAlert({ open: true, message: t('email_template_editor.successful_save') });
      }
    },
    onError: (error) => {
      console.log('error', error.response.data.error);
      setStateAlert({ open: true, message: error.response.data.error, type: 'error' });
    },
  });

  const submitForm = handleSubmit(async (data) => {
    setShowAlert(true);

    setTimeout(() => {
      mutation.mutate({ id: emailTemplateId, data });
    }, 100);
  });

  const submitFormWithoutAlert = handleSubmit(async (data) => {
    setShowAlert(false);
    setTimeout(() => {
      mutation.mutate({ id: emailTemplateId, data });
    }, 100);
  });

  const deleteLanguage = (language) => {
    deleteLanguagePropertiesInForm(language, translatedProperties, watch, setValue);
    submitForm();
  };

  const addLanguage = (language) => {
    if (!language) return;
    addLanguagePropertiesInForm(language, translatedProperties, watch, setValue);
    submitForm();
  };

  const setValueLanguage = (language, property, content) => {
    setValueLanguageInForm(language, property, content, watch, setValue);
    submitForm();
  };

  return {
    state: {
      form: { control, resetField, isDirty },
      query: { data, isLoading },
    },
    actions: { watch, submitForm, deleteLanguage, addLanguage, setValueLanguage },
  };
}
